import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { ArrowLeft, Database, Package, FileText, ClipboardList, Briefcase, Timer, FileBox, DollarSign, Palette, Receipt, Box, Shirt, Store, Lightbulb, Recycle, ClipboardCheck, XCircle } from 'lucide-react';
import { usePermissions } from '@/hooks/use-permissions';

export default function DesignHub({ user }) {
  const navigate = useNavigate();
  const { hasPermission, canView } = usePermissions(user);
  const [accessDeniedDialog, setAccessDeniedDialog] = useState(false);
  const [deniedModule, setDeniedModule] = useState('');

  const designHubModules = [
    { 
      title: 'Master Data Entry', 
      description: 'Manage core business entities', 
      icon: Database, 
      path: '/master-data',
      permission: 'master_data_view'
    },
    { 
      title: 'Raw Goods Inventory', 
      description: 'Track raw materials', 
      icon: Package, 
      path: '/inventory',
      permission: 'inventory_view'
    },
    { 
      title: 'Showroom', 
      description: 'Displayed products information', 
      icon: Store, 
      path: '/finished-goods-inventory',
      permission: 'showroom_view'
    },
    { 
      title: 'Purchase Orders', 
      description: 'Order raw goods for sampling', 
      icon: FileText, 
      path: '/purchase-orders',
      permission: 'purchase_orders_view'
    },
    { 
      title: 'Design HQ', 
      description: 'Inspiration to approval workflow', 
      icon: Lightbulb, 
      path: '/design-studio/fashion-design',
      permission: 'fashion_design_view'
    },
    { 
      title: 'Sampling Time Tracker', 
      description: 'Track sampling time for articles', 
      icon: Timer, 
      path: '/task-management/sampling-time-tracker',
      permission: 'sampling_time_tracker_view'
    },
    { 
      title: 'Wastage', 
      description: 'Track and utilize fabric wastage', 
      icon: Recycle, 
      path: '/design-studio/wastage',
      permission: 'wastage_view'
    },
    { 
      title: 'Gate Pass', 
      description: 'Manage returnable and non-returnable gate passes', 
      icon: ClipboardCheck, 
      path: '/design-studio/gate-pass',
      permission: 'gate_pass_view'
    },
    { 
      title: 'Internal Work Orders', 
      description: 'Manage internal work orders for sampling', 
      icon: FileText, 
      path: '/work-orders',
      permission: 'work_orders_view'
    },
    { 
      title: 'Tech Pack', 
      description: 'Technical packages for approved designs', 
      icon: FileBox, 
      path: '/design-studio/tech-pack',
      permission: 'view_tech_pack'
    },
    { 
      title: 'LN-X Reports', 
      description: 'LN-X reports for finished good designs', 
      icon: FileText, 
      path: '/design-studio/lnx-reports',
      permission: 'lnx_reports_view'
    },
    { 
      title: 'Pre-Production Costing', 
      description: 'Automated costing for approved designs', 
      icon: DollarSign, 
      path: '/design-studio/pre-production-costing',
      permission: 'view_costing'
    },
    { 
      title: 'External Work Orders', 
      description: 'Manage external work orders for sampling', 
      icon: FileText, 
      path: '/external-work-orders',
      permission: 'external_work_orders_view'
    },
    { 
      title: 'Expenses', 
      description: 'Manage company expenses', 
      icon: Receipt, 
      path: '/expenses',
      permission: 'expenses_view'
    },
  ];

  // Handle module click with permission check
  const handleModuleClick = (moduleName, path, requiredPermission) => {
    if (hasPermission(requiredPermission)) {
      navigate(path);
    } else {
      setDeniedModule(moduleName);
      setAccessDeniedDialog(true);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <header className="bg-white shadow-md border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Design Studio</h1>
              <p className="text-sm text-gray-600">Sampling and design management</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {designHubModules.map((module, idx) => {
            const Icon = module.icon;
            return (
              <Card
                key={idx}
                className="hover:shadow-xl transition-all duration-300 cursor-pointer border-2 border-black"
                onClick={() => handleModuleClick(module.title, module.path, module.permission)}
                data-testid={`module-card-${module.path.replace('/', '')}`}
              >
                <CardHeader className="text-center">
                  <div className="flex justify-center mb-4">
                    <div className="w-16 h-16 rounded-lg bg-black flex items-center justify-center">
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                  </div>
                  <CardTitle className="text-xl" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {module.title}
                  </CardTitle>
                  <CardDescription className="text-sm mt-2">
                    {module.description}
                  </CardDescription>
                </CardHeader>
              </Card>
            );
          })}
        </div>
      </main>

      {/* Access Denied Dialog */}
      <Dialog open={accessDeniedDialog} onOpenChange={setAccessDeniedDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
                <XCircle className="w-10 h-10 text-red-600" />
              </div>
            </div>
            <DialogTitle className="text-center text-2xl font-bold">Access Denied</DialogTitle>
            <DialogDescription className="text-center text-base pt-2">
              You do not have permission to access the {deniedModule} module.
              <br />
              <br />
              Please contact your administrator to request access.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-center">
            <Button
              onClick={() => setAccessDeniedDialog(false)}
              className="bg-black hover:bg-gray-800 text-white px-8"
            >
              OK
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
